import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import Select from 'react-select';

const D3VisualComponent = ({ nodes, links, selectedAddress: initialSelectedAddress, canvasRef }) => {
    // const canvasRef = useRef(null);
    const transformRef = useRef(d3.zoomIdentity);
    const simulationRef = useRef(null);

    const [localSelectedAddress, setLocalSelectedAddress] = useState(initialSelectedAddress);

    const [dimensions, setDimensions] = useState({
        width: window.innerWidth * 0.4,
        height: window.innerHeight * 0.9
    });

    const draw = useCallback((currentTransform) => {
        if (!canvasRef.current || !nodes || nodes.length === 0) return;
        const context = canvasRef.current.getContext('2d');
        
        context.clearRect(0, 0, context.canvas.width, context.canvas.height);

        context.save();
        context.translate(currentTransform.x, currentTransform.y);
        context.scale(currentTransform.k, currentTransform.k);
        
        const linkValueScale = d3.scaleLinear()
            .domain(d3.extent(links, d => +d.value))
            .range([1, 10]);
        
        links.forEach(link => {
            context.beginPath();
            context.moveTo(link.source.x, link.source.y);
            context.lineTo(link.target.x, link.target.y);
            context.strokeStyle = link.type === 'send' ? '#FF8C00' : '#32CD32';
            context.globalAlpha = 0.7;
            context.lineWidth = linkValueScale(+link.value);
            context.stroke();
        });

        nodes.forEach(node => {
            context.beginPath();
            context.arc(node.x, node.y, node.id === localSelectedAddress ? 15 : 10, 0, 2 * Math.PI);
            context.fillStyle = node.id === localSelectedAddress ? "red" : "#4682B4";
            context.globalAlpha = 1.0;
            context.fill();

            context.font = "12px sans-serif";
            context.fillStyle = "black";
            context.fillText(node.label, node.x + (node.id === localSelectedAddress ? 20 : 12), node.y);
        });

        context.restore();
    }, [nodes, links, localSelectedAddress, transformRef.current]);

    const simulation = useMemo(() => {
        if (nodes.length === 0 || !canvasRef.current) return null;
        const sim = d3.forceSimulation(nodes)
            .force("link", d3.forceLink(links).id((d) => d.id).distance(150))
            .force("charge", d3.forceManyBody().strength(-300))
            .force("center", d3.forceCenter(dimensions.width / 2, dimensions.height / 2));
        simulationRef.current = sim;
        return sim;
    }, [nodes, links, dimensions]);

    useEffect(() => {
        if (!localSelectedAddress || !canvasRef.current || !simulationRef.current) return;

        const selectedNode = nodes.find(n => n.id === localSelectedAddress);
        if (selectedNode) {
            const canvas = d3.select(canvasRef.current);
            const center_x = dimensions.width / 2;
            const center_y = dimensions.height / 2;
            const new_x = center_x - selectedNode.x;
            const new_y = center_y - selectedNode.y;

            const newTransform = d3.zoomIdentity.translate(new_x, new_y).scale(1);
            canvas.transition().duration(750).call(d3.zoom().transform, newTransform);
            transformRef.current = newTransform;
            draw(newTransform);
        }
    }, [localSelectedAddress, nodes, dimensions, draw]);

    useEffect(() => {
        if (!simulation) return;
        simulation.on("tick", () => {
            draw(transformRef.current);
        });
        return () => {
            simulation.stop();
        };
    }, [simulation, draw]);

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth * 0.4,
                height: window.innerHeight * 0.9
            });
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (!canvasRef.current || !simulation) return;
        const canvas = d3.select(canvasRef.current);
        
        const zoomBehavior = d3.zoom()
            .scaleExtent([0.1, 10])
            .on("zoom", (event) => {
                transformRef.current = event.transform;
                draw(event.transform);
            });
        
        canvas.call(zoomBehavior);

        const drag = (sim) => {
            function dragstarted(event) {
                if (!event.active) sim.alphaTarget(0.3).restart();
                event.subject.fx = transformRef.current.invertX(event.x);
                event.subject.fy = transformRef.current.invertY(event.y);
            }
            
            function dragged(event) {
                event.subject.fx = transformRef.current.invertX(event.x);
                event.subject.fy = transformRef.current.invertY(event.y);
                draw(transformRef.current);
            }
            
            function dragended(event) {
                if (!event.active) sim.alphaTarget(0);
                event.subject.fx = null;
                event.subject.fy = null;
            }
            
            return d3.drag()
                .container(canvas.node())
                .subject((event) => {
                    for (const node of nodes) {
                        const dx = transformRef.current.invertX(event.x) - node.x;
                        const dy = transformRef.current.invertY(event.y) - node.y;
                        if (dx * dx + dy * dy < (15 * 15)) {
                            return node;
                        }
                    }
                    return null;
                })
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended);
        };

        canvas.call(drag(simulation));
    }, [simulation, nodes, draw]);

    const zoomIn = () => {
        d3.select(canvasRef.current).transition().call(d3.zoom().scaleBy, 1.2);
    };
    
    const zoomOut = () => {
        d3.select(canvasRef.current).transition().call(d3.zoom().scaleBy, 0.8);
    };

    const resetZoom = () => {
        d3.select(canvasRef.current).transition().duration(750).call(d3.zoom().transform, d3.zoomIdentity);
    };
    
    const addressOptions = useMemo(() => {
        return nodes.map(node => ({
            value: node.id,
            label: node.label
        }));
    }, [nodes]);

    const handleAddressSelect = (selectedOption) => {
        setLocalSelectedAddress(selectedOption ? selectedOption.value : null);
    };

    const selectedOption = useMemo(() => {
        return addressOptions.find(option => option.value === localSelectedAddress);
    }, [addressOptions, localSelectedAddress]);

    return (
        <div>
            <div style={{ width: `${dimensions.width}px` }}>
                <Select
                    options={addressOptions}
                    onChange={handleAddressSelect}
                    value={selectedOption}
                    placeholder="Search for an address..."
                    isClearable
                />
            </div>
            
            <div className="visual" style={{ border: '1px solid #ccc', width: `${dimensions.width}px`, height: `${dimensions.height}px`}}>
                <canvas 
                    ref={canvasRef} 
                    className="visual-container" 
                    width={dimensions.width} 
                    height={dimensions.height}
                />
            </div>
        </div>
    );
};

export default D3VisualComponent;